import * as React from 'react'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { Sparkles, X, Send } from 'lucide-react'
import { AIEditQueueItem } from '../slides/PropertyPanel'

interface AIEditQueueDisplayProps {
  queue: AIEditQueueItem[]
  onRemove: (id: string) => void
  onSendAll: () => void
  isProcessing?: boolean
  className?: string
}

export const AIEditQueueDisplay: React.FC<AIEditQueueDisplayProps> = ({
  queue,
  onRemove,
  onSendAll,
  isProcessing = false,
  className
}) => {
  if (queue.length === 0) return null

  return (
    <div className={cn("space-y-2", className)}>
      {/* Header with count and send button */}
      <div className="flex items-center justify-between px-1">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-primary dark:text-accent-foreground">
            {queue.length} AI Edit{queue.length !== 1 ? 's' : ''} Queued
          </span>
        </div>
        <Button
          onClick={onSendAll}
          disabled={isProcessing}
          size="sm"
          className="h-7 text-primary-foreground text-xs bg-primary hover:bg-primary/90"
        >
          <Send className="h-3 w-3 mr-1.5" />
          {isProcessing ? 'Sending...' : 'Send to AI'}
        </Button>
      </div>

      {/* Queue items as compact chips/boxes */}
      <div className="flex flex-wrap gap-2">
        {queue.map((item) => (
          <div
            key={item.id}
            className="group inline-flex items-center gap-2 px-3 py-1.5 bg-accent rounded-lg border border-primary/30 max-w-[280px]"
          >
            <div className="flex-1 min-w-0">
              <div className="text-xs font-medium text-accent-foreground truncate">
                {item.elementName}
              </div>
              <div className="text-xs text-primary dark:text-accent-foreground truncate">
                {item.instruction}
              </div>
            </div>
            <button
              onClick={() => onRemove(item.id)}
              disabled={isProcessing}
              className="flex-shrink-0 p-1 rounded hover:bg-accent dark:hover:bg-accent text-primary hover:text-accent-foreground dark:hover:text-accent-foreground transition-colors disabled:opacity-50"
              aria-label={`Remove ${item.elementName} edit`}
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        ))}
      </div>
    </div>
  )
}
